/**
 * Forage Model — scoring reference for the PRF Research tab.
 *
 * Chrome is lifted verbatim from the Scoring Model tab in App.js so the two read as one
 * system:
 *   · hero card   — border C.navy, solid navy header bar, white title + 60%-white subtitle
 *   · panel card  — border C.border, title over a 2px navy underline sized to content
 *   · tables      — 11px body; 10px uppercase headers, 0.07em tracking, 5px 10px padding;
 *                   rows zebra on C.bg with a 1px C.border rule; cells 8px 10px, top-aligned
 *   · metric rows — label 11/700, key 9 monospace, description 8/1.5 capped at 280px,
 *                   source/vintage/unit 10 muted nowrap, dimension as a tinted pill
 *   · weight bar  — navy bar `weight * 2` px wide beside the percentage
 *
 * Content tracks ground-score-pipeline/prf_score.py and docs/prf-research-scope.md §4/§12–14.
 */

import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { C } from "./data";

export const DIMS = [
  {
    id: "subsidy_yield",
    label: "Subsidy Yield",
    weight: 25,
    metrics: [["Expected RMA Transfer per $ of Land", "higher → higher score"]],
  },
  {
    id: "forage_productivity",
    label: "Forage Productivity",
    weight: 15,
    metrics: [["Pasture Cash Rent", "higher → higher score"]],
  },
  {
    id: "land_basis",
    label: "Land Basis",
    weight: 17,
    metrics: [["Pastureland Value per Acre", "lower → higher score"]],
  },
  {
    id: "hedge_quality",
    label: "Hedge Quality",
    weight: 12,
    metrics: [
      ["Normal Precip, Selected Intervals", "higher → higher score"],
      ["Index Coefficient of Variation", "lower → higher score"],
    ],
  },
  {
    id: "cbv_boundary",
    label: "CBV Boundary Capture",
    weight: 10,
    metrics: [["Max CBV Uplift within Grid", "higher → higher score"]],
  },
  {
    id: "water_drought",
    label: "Water & Drought",
    weight: 7,
    metrics: [["Severe Drought Frequency (D2+)", "lower → higher score"]],
  },
  {
    id: "market_access",
    label: "Cattle Market Access",
    weight: 6,
    metrics: [
      ["Distance to Feeding Center", "lower → higher score"],
      ["Beef Cow Density", "higher → higher score"],
    ],
  },
  {
    id: "federal_grazing",
    label: "Federal Grazing",
    weight: 5,
    metrics: [["BLM Allotment Acres Nearby", "higher → higher score"]],
  },
  {
    id: "operating_reg",
    label: "Operating & Regulatory",
    weight: 3,
    metrics: [["Effective Property Tax Rate", "lower → higher score"]],
  },
];

// [key, label, source, vintage, unit, description, dimension]
export const METRIC_INDEX = [
  [
    "base_rate",
    "PRF Premium Rate",
    "RMA Actuarial Data Master",
    "RY2026",
    "% of liability",
    "Grid × interval × coverage level. Verified across all 725,020 combinations: zero carry more than one rate, so the rate is a pure function of the grid.",
    "Subsidy Yield",
  ],
  [
    "cbv",
    "County Base Value",
    "RMA ADM · A00810 Price",
    "RY2026",
    "$/acre",
    "Dollar value of production per acre, set per county and intended use. Grazing median $47 · hay non-irrigated $133 · hay irrigated $243.",
    "CBV Boundary Capture",
  ],
  [
    "index",
    "Rainfall Index History",
    "RMA GetIndexValues",
    "1948–2026",
    "% of normal",
    "Full series for all 11 bi-monthly intervals per grid. Supplies both the trailing burn cost and the index denominator.",
    "Allocation & reporting",
  ],
  [
    "normal",
    "Normal Interval Precipitation",
    "RMA AverageIntervalMeasurement",
    "1948–2026",
    "index units",
    "The index denominator itself. A near-zero normal makes the index swing in percentage terms without tracking real forage loss.",
    "Hedge Quality",
  ],
  [
    "cv",
    "Index Coefficient of Variation",
    "Derived from RMA index",
    "1948–2026",
    "ratio",
    "Dispersion of the index about its mean. Runaway CV is the low-denominator signature seen from the other side.",
    "Hedge Quality",
  ],
  [
    "pasture_rent_acre",
    "Pasture Cash Rent",
    "USDA NASS Cash Rents",
    "2023–25",
    "$/acre/yr",
    "Market price of grass — a renter pays in proportion to the forage present. County tier for 2,289 counties; state mean elsewhere, tagged in the payload.",
    "Forage Productivity",
  ],
  [
    "pasture_value_acre",
    "Pastureland Value",
    "USDA NASS · state tier",
    "2024–26",
    "$/acre",
    "State value scaled by the observed county-to-state rent ratio, clamped to [0.4, 2.5]; flat state value where no county rent exists.",
    "Land Basis",
  ],
  [
    "rent_per_aum",
    "Grazing Rent per AUM",
    "USDA NASS Cash Rents",
    "2023–25",
    "$/AUM/mo",
    "State tier, 16 western states. Drives implied carrying capacity in the deal underwriter.",
    "Supplemental only",
  ],
  [
    "d2_freq",
    "Severe Drought Frequency",
    "US Drought Monitor (NDMC)",
    "2001–25",
    "% of weeks",
    "Share of weeks with any D2+ coverage, from 1,306 weekly observations per county.",
    "Water & Drought",
  ],
  [
    "cattle_on_feed",
    "Cattle on Feed",
    "USDA NASS",
    "2022–24",
    "head",
    "Top-decile counties (≥14,428 head) define the feeding centers that set haul distance.",
    "Cattle Market Access",
  ],
  [
    "beef_cows",
    "Beef Cow Inventory",
    "USDA NASS",
    "2022–24",
    "head/sq mi",
    "Local cow-calf base; proxies the sale barns, vets and haulers that cluster where cows are.",
    "Cattle Market Access",
  ],
  [
    "blm_acres",
    "BLM Allotment Acres",
    "BLM National Grazing Allotments",
    "current",
    "acres",
    "21,782 allotments mapped to grids. Carries acreage, not permitted AUMs — those live in RAS, which has no open endpoint.",
    "Federal Grazing",
  ],
  [
    "property_tax_rate",
    "Effective Property Tax",
    "Census ACS 5-yr",
    "2022",
    "%",
    "Median real estate taxes ÷ median home value. Residential, not agricultural use-value, so read it as taxing posture rather than an expense.",
    "Operating & Regulatory",
  ],
  [
    "loss_ratio",
    "Realized PRF Loss Ratio",
    "RMA Summary of Business",
    "2010–25",
    "ratio",
    "County indemnities ÷ premiums across 1.9bn acre-years. The national figure is 0.990.",
    "Reported only — not scored",
  ],
];

/**
 * Model progression — the audit trail for every change to the scoring model.
 *
 * ⚠️ Append-only. A superseded entry is never edited or deleted, because the whole point of
 * this section is that a reader can see what we believed, what we tested, and what the test
 * made us change. Deleting a wrong turn deletes the evidence that we check our work.
 *
 * Each entry reads: what we believed → how it was tested → what the data said → what changed.
 * `evidence` carries the specific figures, so any claim traces back to a number rather than
 * to a conviction.
 */
export const MODEL_PROGRESSION = [
  {
    v: "v3",
    date: "Aug 2026",
    status: "current",
    title: "Rate Mispricing retired — the rate surface is efficiently priced",
    hypothesis:
      "That RMA sometimes prices a grid wrong and leaves it wrong, so a grid whose recent payouts exceeded its premium would keep over-paying. Rate Mispricing carried 8 of 100 points on that premise: half the trailing-30yr burn cost minus the published rate, half the realized county loss ratio.",
    test:
      "Four independent out-of-sample tests against the RMA Summary of Business — 947 counties carrying rainfall-index premium in both halves of 2010–2025, vegetation-index rows excluded.",
    evidence: [
      "Past county loss ratio does not predict future loss ratio: R² 0.0016, t 1.2. Sorting counties across a 5.8× spread in past loss ratio produced only a 0.93 → 1.11 spread in what actually followed.",
      "Not a sample-size problem — persistence stays at zero through every premium-volume quintile, from $245k up to $14.0M.",
      "RMA does not re-rate off loss experience: past loss ratio explains 0.03% of the 2026 published rate. They rate off rainfall — trailing burn cost alone explains 84.9% of it.",
      "The rate's own deviation from burn does not predict losses either: t 1.8.",
      "Selecting intervals on markup LOSES out of sample. Picking on 1948–95 burn and scoring on 1996–2026 gave burn ÷ rate of 0.655, against 0.861 for the existing max-rate rule. The apparent free lunch — an in-sample allocation priced below its own burn cost — was selection bias across roughly forty legal interval pairs.",
    ],
    change:
      "Dimension removed. Its 8 points went to Subsidy Yield (20 → 25) and Land Basis (14 → 17), the two dimensions measuring the CERTAIN 51–59% transfer and the price paid for it — neither of which needs RMA to be wrong about anything. Burn, mispricing and loss ratio are still computed and shown on the report as figures; they are simply no longer scored.",
    caveat:
      "This raises concentration. Subsidy Yield already drove 45% of the score's variance off 20% of the weight, because it correlates +0.59 with Land Basis — pastureland value sits in its denominator. Residualizing rent against value is the candidate fix and has not been done yet.",
  },
  {
    v: "v2.1",
    date: "Aug 2026",
    status: "current",
    title: "Coverage level selected on the payoff the ranking actually uses",
    hypothesis:
      "That maximizing subsidy per dollar of protection also maximizes subsidy per acre. The optimizer ranked coverage levels on eff_rate × subsidy_pct.",
    test:
      "Re-solved all 13,462 grids with the coverage-level factor folded into the maximand, and compared which level won.",
    evidence: [
      "The two objectives disagree on 5,394 of 13,462 grids — 40.1%. Subsidy per acre was understated on those by a mean of 4.42%, median 4.10%, max 15.13%.",
      "Both quantities are internally coherent and differ by exactly the coverage-level factor. But subsidy per acre is what Subsidy Yield and SGY are built from, so that is the one the selection has to match.",
    ],
    change:
      "The objective is now cl × eff_rate × subsidy_pct, matching the payoff the score reports.",
  },
  {
    v: "v2",
    date: "Aug 2026",
    status: "current",
    title: "Hedge quality measured instead of proxied",
    hypothesis:
      "That the low-denominator trap — an index that swings wildly in percentage terms without tracking any real forage loss — could be inferred from rate spread alone, as Phase 1 did.",
    test:
      "Pulled the full 1948–2026 index history for all 13,462 grids from RMA GetIndexValues, including the AverageIntervalMeasurement that is the index denominator itself.",
    evidence: [
      "Normal interval precipitation and the index coefficient of variation are now measured directly rather than inferred, and form the two halves of Hedge Quality.",
      "The same pull supplies the trailing-30yr burn cost that drives the Max Expected Value and Best Hedge allocation objectives.",
    ],
    change:
      "Dimensions expanded from the Phase 1 subset to all ten, with drought, cattle market access, federal grazing and operating friction added alongside the measured hedge.",
  },
  {
    v: "v2",
    date: "Aug 2026",
    status: "current",
    title: "Coverage floor added — thin data no longer ranks Lead Market",
    hypothesis:
      "That letting a missing metric shrink the weight pool, rather than score zero, was sufficient protection against data gaps.",
    test: "Inspected the top of the ranking directly (scope doc §13.3).",
    evidence: [
      "Grids with no land data at all were scoring off the thin dimensions alone and landing in Lead Market — the highest tier — on a handful of points.",
      "NASS pasture coverage reaches only about 2,285 of 3,071 counties, so the gap is systematic rather than incidental.",
    ],
    change:
      "Subsidy Yield and Land Basis are now required, plus ≥60% of the weight pool, or the grid is left unscored and tiered insufficient_data. 99.4% of grids carry the full weight pool today.",
  },
  {
    v: "v2",
    date: "Aug 2026",
    status: "current",
    title: "Pastureland value imputed from county rent, with a state-mean backfill",
    hypothesis: "That NASS pastureland value could be used as published.",
    test: "Checked its coverage against the scored county universe.",
    evidence: [
      "NASS publishes pastureland asset value at STATE tier only, while cash rent is published for roughly 2,300 counties. Rent coverage is thinnest exactly where rangeland matters — Nevada 4 of 17 counties, Arizona 11, Utah 28.",
      "Those counties were previously dropped as 'no pasture data', graying out most of Nevada and chunks of California, Arizona and Utah.",
    ],
    change:
      "county_value = state_value × clamp(county_rent ÷ state_mean_rent, 0.4, 2.5), with a flat state value where no county rent exists. Provenance is tagged per county (county_rent or state_mean) and carried into the payload so a reader can see which they are looking at.",
    caveat:
      "This makes Land Basis and Forage Productivity partly the same measurement with opposite signs — they correlate −0.70. It is the largest known structural issue in the model, and the reason residualization is the next candidate change.",
  },
  {
    v: "v1",
    date: "Aug 2026",
    status: "superseded",
    title: "One rate surface, verified — solve per grid, not per grid-county-segment",
    hypothesis:
      "That the premium rate might vary by county or by intended use, requiring a separate solve for every grid-county-segment row.",
    test:
      "Checked every (grid, interval, coverage level) combination in the RY2026 Actuarial Data Master for conflicting rates.",
    evidence: [
      "Across all 725,020 combinations, ZERO carry more than one distinct base rate. The rate is a pure function of the grid, invariant to both county and intended use.",
      "Only County Base Value differs by segment — which is why the three segments share a rate surface but not a score.",
    ],
    change:
      "The optimizer runs once per grid (13,462 passes instead of about 91,000), and grid geometry plus rate data are hoisted into a single shared block in the payload rather than repeated per segment.",
  },
  {
    v: "v1",
    date: "Aug 2026",
    status: "superseded",
    title: "Screen for the transfer, not for arbitrage",
    hypothesis: "That grid-level rate arbitrage would be the primary source of return.",
    test: "Phase 0 measured realized program economics across the full Summary of Business.",
    evidence: [
      "National loss ratio 2010–2025 is 0.990 across 1.9 billion acre-years — underwriting gain is indistinguishable from zero.",
      "Realized producer net was $2.37/acre/yr: $2.42 of subsidy transfer minus $0.05 of underwriting loss. Essentially all producer profit IS the transfer.",
      "Cross-sectional mispricing persisted only weakly — top-quintile county selection worth about +$0.71/acre/yr, roughly a quarter of the subsidy engine.",
      "Rates barely move: the 2023 and 2026 ADM filings correlate 0.9992, County Base Values did not change across three years, and the optimal grid election is unchanged for 88% of grids. There is no re-rating cycle to time.",
    ],
    change:
      "Weights were set so the transfer-capture dimensions dominate, with mispricing kept deliberately small. v3 finished the job and removed it entirely.",
  },
];

export const METHODOLOGY = [
  [
    "Universe",
    "All 13,462 CONUS rainfall grids are scored simultaneously, three times over — grazing, hay non-irrigated and hay irrigated. Scores are universe-relative percentiles: 72 means the grid ranks in the 72nd percentile of its peer set, not an absolute value.",
  ],
  [
    "Normalization",
    "Each raw metric is percentile-ranked 0–100 within the scored universe. Direction is applied (lower_better metrics are inverted). Metrics within a dimension are simple-averaged into a dimension score.",
  ],
  [
    "Composite",
    "The Ground Score is a weighted average of available dimensions: Σ(dim_score × weight) ÷ Σ(weights). A missing metric shrinks the denominator rather than scoring zero, so data gaps do not artificially depress a grid.",
  ],
  [
    "Coverage Floor",
    "Subsidy Yield and Land Basis are required, plus ≥60% of the weight pool. Without this a grid with no land data scored off the thin dimensions alone and ranked Lead Market.",
  ],
  [
    "Tiers",
    "Percentile-based within the scored universe. Lead Market ≥ 95th pct · Priority Market ≥ 80th · Watchlist ≥ 50th · Deprioritized < 50th.",
  ],
  [
    "One Rate, Three Segments",
    "Premium rates are identical across intended use — verified across all 725,020 grid × interval × coverage combinations. Only County Base Value differs, which is why the segments share a rate surface but not a score.",
  ],
];

export const OBJECTIVES = [
  [
    "Max Subsidy",
    "max Σ(alloc × rate)",
    "Pure transfer capture. The default, and what the ranking is built on.",
  ],
  [
    "Max Expected Value",
    "max Σ(alloc × (burn − rate × (1 − subsidy)))",
    "Takes the regime-drift view using the trailing-30yr empirical burn.",
  ],
  [
    "Best Hedge",
    "weight by denominator strength and index stability",
    "For an operator who needs the index to track real forage loss, not merely to pay.",
  ],
];

export const PROGRAM = [
  ["Subsidy schedule", "90% → 51% · 80–85% → 55% · 70–75% → 59%"],
  ["Productivity factor", "60%–150% of County Base Value"],
  [
    "Interval allocation",
    "≥2 non-overlapping intervals, 10–60% each, totalling 100%",
  ],
  ["Index intervals", "11 bi-monthly (Jan–Feb … Nov–Dec)"],
  ["Grid", "0.25° × 0.25° (~17 mi) · 13,462 in CONUS"],
  ["Sales closing", "December 1 — also the acreage reporting date"],
];

/* ── chrome shared with the Scoring Model tab ────────────────────────────── */

const TH = {
  padding: "5px 10px",
  textAlign: "left",
  color: C.muted,
  fontWeight: 700,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  borderBottom: `1px solid ${C.border}`,
  whiteSpace: "nowrap",
};

const rowStyle = (i) => ({
  background: i % 2 === 0 ? "transparent" : C.bg,
  borderBottom: `1px solid ${C.border}`,
});

const TD = { padding: "8px 10px", verticalAlign: "top" };
const TD_META = { ...TD, whiteSpace: "nowrap", fontSize: 10, color: C.muted };

const PanelHeader = ({ title, sub }) => (
  <Box
    sx={{
      pb: 1,
      mb: 1.5,
      borderBottom: `2px solid ${C.navy}`,
      width: "fit-content",
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 800,
        color: C.navy,
        textTransform: "uppercase",
        letterSpacing: "0.09em",
      }}
    >
      {title}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const Panel = ({ title, sub, children }) => (
  <Card
    elevation={0}
    sx={{ border: `1px solid ${C.border}`, borderRadius: 1, mb: 2.5 }}
  >
    <CardContent sx={{ pb: "12px !important" }}>
      <PanelHeader title={title} sub={sub} />
      {children}
    </CardContent>
  </Card>
);

export default function PrfScoringModel() {
  // Collapsed by default — the log is a depth surface, not something to scroll past.
  const [logOpen, setLogOpen] = React.useState(false);

  return (
    <Box>
      {/* ── two columns ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          alignItems: "flex-start",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ flex: { md: "0 0 50%" }, minWidth: 0, width: "100%" }}>
          {/* ── hero: methodology ───────────────────────────────────────── */}
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${C.navy}`,
              borderRadius: 1,
              overflow: "hidden",
              mb: 2.5,
            }}
          >
            <Box sx={{ background: C.navy, px: 2, py: 1.25 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: C.white,
                  textTransform: "uppercase",
                  letterSpacing: "0.09em",
                }}
              >
                Forage Model · Scoring Methodology
              </Typography>
              <Typography
                sx={{ fontSize: 10, color: "rgba(255,255,255,0.6)", mt: 0.25 }}
              >
                How every Ground Score, dimension and tier is computed from raw
                data
              </Typography>
            </Box>
            <CardContent sx={{ pb: "12px !important" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {METHODOLOGY.map(([title, body]) => (
                  <Box key={title} sx={{ flex: "1 1 260px", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: C.navy,
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        mb: 0.5,
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.65 }}
                    >
                      {body}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
          <Panel
            title="Forage Markets Thesis · Dimensions"
            sub="Ground Score = Σ(dim score × weight) ÷ Σ weights · all scores 0–100 percentile within universe"
          >
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 11,
                }}
              >
                <thead>
                  <tr>
                    {["Dimension", "Wt", "Input Metrics & Direction"].map(
                      (h) => (
                        <th key={h} style={TH}>
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {DIMS.map((d, i) => (
                    <tr key={d.id} style={rowStyle(i)}>
                      <td
                        style={{
                          ...TD,
                          fontWeight: 700,
                          color: C.charcoal,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {d.label}
                      </td>
                      <td style={{ ...TD, whiteSpace: "nowrap" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: `${d.weight * 2}px`,
                              height: 6,
                              background: C.navy,
                              borderRadius: 2,
                              minWidth: 4,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            {d.weight}%
                          </span>
                        </Box>
                      </td>
                      <td style={TD}>
                        {d.metrics.map(([name, dir]) => (
                          <Box
                            key={name}
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 1,
                              mb: "3px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                color: C.charcoal,
                                fontWeight: 600,
                              }}
                            >
                              {name}
                            </span>
                            <span style={{ fontSize: 10, color: C.muted }}>
                              {dir}
                            </span>
                          </Box>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Panel>

          <Panel
            title="Interval Allocation · Objectives"
            sub="All three are solved per grid and shipped in the dataset"
          >
            {OBJECTIVES.map(([name, formula, body]) => (
              <Box key={name} sx={{ mb: 1.5 }}>
                <Typography
                  sx={{ fontSize: 11, fontWeight: 700, color: C.charcoal }}
                >
                  {name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    color: C.navy,
                    fontFamily: "monospace",
                    mb: 0.25,
                  }}
                >
                  {formula}
                </Typography>
                <Typography
                  sx={{ fontSize: 11, color: C.muted, lineHeight: 1.65 }}
                >
                  {body}
                </Typography>
              </Box>
            ))}
          </Panel>

          <Panel
            title="Headline Metric · SGY"
            sub="Reported alongside and separately from the 0–100 Ground Score"
          >
            <Typography
              sx={{
                fontSize: 11,
                color: C.navy,
                fontFamily: "monospace",
                mb: 1,
              }}
            >
              SGY = (pasture rent + E[PRF subsidy]) ÷ pasture value per acre
            </Typography>
            <Typography
              sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.65 }}
            >
              An annual cash-on-land yield. It is the number a buyer actually
              underwrites, and it is directly comparable across grids where a
              percentile rank is not. Gross of ownership cost — property tax,
              fencing and water development are not assumed.
            </Typography>
          </Panel>

          {/* ── Model progression ────────────────────────────────────────
              ⚠️ Lives inside the left column, not across the page (§9 — no content block
              spans full width). Collapsed by default: this is the surface a reader opens
              when they want to know whether the weights are considered or merely asserted,
              not something they should have to scroll past to reach the metric index.
              Entries are append-only — superseded ones stay, because a retracted hypothesis
              is the evidence that the model gets tested rather than defended. */}
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${C.navy}`,
              borderRadius: 1,
              overflow: "hidden",
              mb: 2.5,
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setLogOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setLogOpen((v) => !v);
                }
              }}
              sx={{
                background: C.navy,
                px: 2,
                py: 1.25,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                userSelect: "none",
                "&:hover": { background: "#0d2b50" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: C.white,
                    textTransform: "uppercase",
                    letterSpacing: "0.09em",
                  }}
                >
                  Model Progression
                </Typography>
                <Typography
                  sx={{ fontSize: 10, color: "rgba(255,255,255,0.6)", mt: 0.25 }}
                >
                  {logOpen
                    ? "Every change to the model, the hypothesis behind it, and the evidence that settled it"
                    : `${MODEL_PROGRESSION.length} entries — what we tested and what changed`}
                </Typography>
              </Box>
              <Box
                component="svg"
                viewBox="0 0 24 24"
                aria-hidden
                sx={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  color: "rgba(255,255,255,0.75)",
                  transform: logOpen ? "rotate(180deg)" : "none",
                  transition: "transform 120ms ease",
                }}
              >
                <path
                  d="M6 9l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            </Box>

            {logOpen && (
              <CardContent sx={{ pb: "12px !important" }}>
                {MODEL_PROGRESSION.map((e, i) => (
                  <Box
                    key={`${e.v}-${e.title}`}
                    sx={{
                      py: 1.75,
                      borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                        mb: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          px: 0.75,
                          py: "2px",
                          borderRadius: 0.5,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.04em",
                          color: e.status === "current" ? C.white : C.muted,
                          background:
                            e.status === "current" ? C.navy : "transparent",
                          border:
                            e.status === "current"
                              ? "none"
                              : `1px solid ${C.border}`,
                        }}
                      >
                        {e.v}
                      </Box>
                      <Typography sx={{ fontSize: 10, color: C.muted }}>
                        {e.date}
                        {e.status === "superseded" ? " · superseded" : ""}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: C.navy,
                        mb: 0.75,
                      }}
                    >
                      {e.title}
                    </Typography>

                    {[
                      ["Hypothesis", e.hypothesis],
                      ["How it was tested", e.test],
                    ].map(([label, body]) => (
                      <Box key={label} sx={{ mb: 0.75 }}>
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.muted,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: C.charcoal,
                            lineHeight: 1.55,
                          }}
                        >
                          {body}
                        </Typography>
                      </Box>
                    ))}

                    <Box sx={{ mb: 0.75 }}>
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                        }}
                      >
                        What the data said
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                        {e.evidence.map((x, j) => (
                          <Box
                            component="li"
                            key={j}
                            sx={{
                              fontSize: 11,
                              color: C.charcoal,
                              lineHeight: 1.55,
                              mb: 0.25,
                            }}
                          >
                            {x}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ borderLeft: `2px solid ${C.navy}`, pl: 1.25, mt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: C.navy,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                        }}
                      >
                        What changed
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11, color: C.charcoal, lineHeight: 1.55 }}
                      >
                        {e.change}
                      </Typography>
                    </Box>

                    {e.caveat && (
                      <Box
                        sx={{ borderLeft: `2px solid ${C.red}`, pl: 1.25, mt: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.red,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                          }}
                        >
                          Known limitation
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: C.charcoal,
                            lineHeight: 1.55,
                          }}
                        >
                          {e.caveat}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </CardContent>
            )}
          </Card>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Panel
            title="Complete Metric Index"
            sub={`All ${METRIC_INDEX.length} raw data inputs — sources, vintages, units, and which dimension they feed`}
          >
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 11,
                }}
              >
                <thead>
                  <tr>
                    {[
                      "Metric",
                      "Source",
                      "Vintage",
                      "Unit",
                      "Forage Dim(s)",
                    ].map((h) => (
                      <th key={h} style={TH}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRIC_INDEX.map(
                    ([key, label, source, vintage, unit, desc, dim], i) => {
                      const isSupplemental = dim === "Supplemental only";
                      return (
                        <tr key={key} style={rowStyle(i)}>
                          <td style={TD}>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: C.charcoal,
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: C.muted,
                                fontFamily: "monospace",
                                marginTop: 1,
                                marginBottom: 3,
                              }}
                            >
                              {key}
                            </div>
                            <div
                              style={{
                                fontSize: 8,
                                color: C.muted,
                                lineHeight: 1.5,
                                maxWidth: 280,
                              }}
                            >
                              {desc}
                            </div>
                          </td>
                          <td style={TD_META}>{source}</td>
                          <td style={TD_META}>{vintage}</td>
                          <td style={TD_META}>{unit}</td>
                          <td style={{ ...TD, minWidth: 80, maxWidth: 130 }}>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: isSupplemental ? C.muted : C.blue,
                                background: isSupplemental
                                  ? "transparent"
                                  : C.blue + "18",
                                borderRadius: 3,
                                padding: isSupplemental ? "0" : "2px 6px",
                                display: "inline-block",
                                lineHeight: 1.4,
                              }}
                            >
                              {dim}
                            </span>
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </Box>
          </Panel>

          <Panel
            title="Program Constants · RMA"
            sub="Rainfall Index Insurance Standards Handbook FCIC-18150"
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 11,
              }}
            >
              <tbody>
                {PROGRAM.map(([k, v], i) => (
                  <tr key={k} style={rowStyle(i)}>
                    <td style={{ ...TD, color: C.muted, whiteSpace: "nowrap" }}>
                      {k}
                    </td>
                    <td style={{ ...TD, color: C.charcoal, fontWeight: 600 }}>
                      {v}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </Box>
      </Box>

    </Box>
  );
}
